import { useRef, useState, useEffect } from "react";

interface UseHorizontalScrollbarReturn {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  scrollThumbLeft: number;
  scrollThumbWidth: number;
  showScrollbar: boolean;
  handleThumbMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const useHorizontalScrollbar = (data: unknown[]): UseHorizontalScrollbarReturn => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollThumbLeft, setScrollThumbLeft] = useState(0);
  const [scrollThumbWidth, setScrollThumbWidth] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);

  // Update custom scrollbar position and dimensions
  const updateScrollbar = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const hasScroll = scrollWidth > clientWidth;
      setShowScrollbar(hasScroll);

      if (hasScroll) {
        const thumbWidth = (clientWidth / scrollWidth) * clientWidth;
        const thumbLeft = (scrollLeft / scrollWidth) * clientWidth;
        setScrollThumbWidth(thumbWidth);
        setScrollThumbLeft(thumbLeft);
      }
    }
  };

  useEffect(() => {
    updateScrollbar();
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => updateScrollbar();
    const handleResize = () => updateScrollbar();

    // Handle horizontal scroll with mouse wheel - using native event listener with passive: false
    const handleWheel = (e: WheelEvent) => {
      // Check if the event target is inside a scrollable table cell
      const target = e.target as HTMLElement;
      const scrollableCell = target.closest(".table-cell-content");

      // If scrolling vertically inside a table cell with vertical scroll, allow it
      if (scrollableCell && e.deltaY !== 0 && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        const cellElement = scrollableCell as HTMLElement;
        const canScrollUp = cellElement.scrollTop > 0;
        const canScrollDown =
          cellElement.scrollTop < cellElement.scrollHeight - cellElement.clientHeight;

        // Allow vertical scrolling within the cell
        if ((e.deltaY > 0 && canScrollDown) || (e.deltaY < 0 && canScrollUp)) {
          return; // Don't prevent default, allow natural vertical scroll in cell
        }
      }

      if (scrollContainerRef.current && Math.abs(e.deltaX) > 0) {
        e.preventDefault();
        scrollContainerRef.current.scrollLeft += e.deltaX;
      } else if (scrollContainerRef.current && e.deltaY !== 0 && e.shiftKey) {
        // Only convert vertical scroll to horizontal when Shift key is pressed
        // Check if we can scroll horizontally
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        const canScrollLeft = scrollLeft > 0;
        const canScrollRight = scrollLeft < scrollWidth - clientWidth;

        if (canScrollLeft || canScrollRight) {
          e.preventDefault();
          scrollContainerRef.current.scrollLeft += e.deltaY;
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    container.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      container.removeEventListener("wheel", handleWheel);
      window.removeEventListener("resize", handleResize);
    };
  }, [data]);

  const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startScrollLeft = scrollContainerRef.current?.scrollLeft || 0;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (scrollContainerRef.current) {
        const deltaX = moveEvent.clientX - startX;
        const scrollRatio =
          scrollContainerRef.current.scrollWidth / scrollContainerRef.current.clientWidth;
        scrollContainerRef.current.scrollLeft = startScrollLeft + deltaX * scrollRatio;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return {
    scrollContainerRef,
    scrollThumbLeft,
    scrollThumbWidth,
    showScrollbar,
    handleThumbMouseDown,
  };
};
